import React from 'react';
import type { CandidateWorkFeedItem } from '@axiom/validation';
import {
  Brief,
  CondensedHeader,
  CondensedLabel,
  CondensedMedium,
  DateUtil,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Layout,
  LayoutItem,
  ReadonlyTextarea,
  SmallHeader,
  useApi,
  PositionUtil,
} from '@axiom/ui';

import { LocationUtil } from '../../utils/location-util';
import { WorkFeedUtil } from '../../utils/WorkFeedUtil';
import { TaxonomyApi } from '../../api/taxonomy-api';

export type WorkFeedEOIJobDescriptionProps = {
  workFeedItem: CandidateWorkFeedItem;
  name?: string;
};

export const WorkFeedEOIJobDescription = ({
  workFeedItem,
  name = 'WORK_FEED_EOI_JOB_DESCRIPTION',
}: WorkFeedEOIJobDescriptionProps) => {
  const [{ data: taxonomy }] = useApi(TaxonomyApi.readTaxonomy());

  const languagesDisplay = WorkFeedUtil.getLanguagesDisplay(
    workFeedItem.opportunity
  );
  const isLanguageShowing =
    !WorkFeedUtil.isUSBased(workFeedItem.opportunity) &&
    languagesDisplay.length > 0;

  return (
    <div data-test={name}>
      <SmallHeader>Job description</SmallHeader>
      <Gutter bottom="16px" />
      <Grid columns={2}>
        <GridRow>
          <GridColumn>
            <Layout direction="vertical" verticalGutter="4px">
              <LayoutItem>
                <CondensedLabel>Estimated Start Date</CondensedLabel>
                <CondensedMedium name="ESTIMATED_START_DATE">
                  {DateUtil.displayDate(workFeedItem.position.startDate) ??
                    '--'}
                </CondensedMedium>
              </LayoutItem>
              <LayoutItem>
                <CondensedLabel>Estimated Length</CondensedLabel>
                <CondensedMedium name="ESTIMATED_LENGTH">
                  {PositionUtil.getEngagementLengthGrouped(
                    workFeedItem.position
                  ) ?? '--'}
                </CondensedMedium>
              </LayoutItem>
              <LayoutItem>
                <CondensedLabel>Weekly Commitment</CondensedLabel>
                <CondensedMedium name="WEEKLY_COMMITMENT">
                  {PositionUtil.getHoursPerWeek(workFeedItem.position) ?? '--'}
                </CondensedMedium>
              </LayoutItem>
              {isLanguageShowing ? (
                <LayoutItem>
                  <CondensedLabel>Language</CondensedLabel>
                  <CondensedMedium name="LANGUAGE">
                    {languagesDisplay}
                  </CondensedMedium>
                </LayoutItem>
              ) : null}
            </Layout>
          </GridColumn>
          <GridColumn>
            <Layout direction="vertical" verticalGutter="4px">
              <LayoutItem>
                <CondensedLabel>Location</CondensedLabel>
                <CondensedMedium name="LOCATION">
                  {LocationUtil.displayFormattedJobLocation(
                    workFeedItem.opportunity,
                    workFeedItem.position
                  )}
                </CondensedMedium>
              </LayoutItem>
              <LayoutItem>
                <CondensedLabel>Practice Area</CondensedLabel>
                <CondensedMedium name="PRACTICE_AREA">
                  {(workFeedItem.opportunity.primaryPracticeArea &&
                    taxonomy.practiceAreas[
                      workFeedItem.opportunity.primaryPracticeArea
                    ]?.fullName) ||
                    '--'}
                </CondensedMedium>
              </LayoutItem>
              <LayoutItem>
                <CondensedLabel>Legal Role</CondensedLabel>
                <CondensedMedium name="LEGAL_ROLE">
                  {(workFeedItem.position.businessTitle &&
                    taxonomy.businessTitles[workFeedItem.position.businessTitle]
                      ?.displayName) ||
                    '--'}
                </CondensedMedium>
              </LayoutItem>
            </Layout>
          </GridColumn>
        </GridRow>
      </Grid>
      <Gutter bottom="16px" />
      <CondensedHeader>Work Description</CondensedHeader>
      <Brief briefMaxHeight="200px">
        <ReadonlyTextarea
          name="JOB_DESCRIPTION"
          value={workFeedItem.position.description ?? '--'}
          formatting="modern"
        />
      </Brief>
    </div>
  );
};
